import React, { ReactNode, useRef, useEffect, useState } from 'react';

export interface PillTab {
  id: string;
  label: string;
  badge?: ReactNode;
  disabled?: boolean;
  tooltip?: string;
}

export interface PillTabsProps {
  tabs: PillTab[];
  activeTab: string;
  onChange: (tabId: string) => void;
  className?: string;
}

export const PillTabs: React.FC<PillTabsProps> = ({
  tabs,
  activeTab,
  onChange,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });
  const [isReady, setIsReady] = useState(false);
  // When true, indicator snaps without animating — used for resize so it tracks
  // the cursor/layout instead of lagging behind a 300ms transition.
  const [snap, setSnap] = useState(false);

  // Animate on tab change
  useEffect(() => {
    setSnap(false);
    const raf = requestAnimationFrame(() => {
      const el = tabRefs.current[activeTab];
      if (el) {
        setIndicatorStyle({ width: el.offsetWidth, left: el.offsetLeft });
        setIsReady(true);
      } else {
        setIsReady(false);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [activeTab, tabs]);

  // Snap on resize (container, tabs, or window)
  useEffect(() => {
    let snapResetTimer: ReturnType<typeof setTimeout> | null = null;
    const measureSnap = () => {
      const el = tabRefs.current[activeTab];
      if (!el) return;
      setSnap(true);
      setIndicatorStyle({ width: el.offsetWidth, left: el.offsetLeft });
      setIsReady(true);
      // Re-enable the transition shortly after resize settles so subsequent
      // tab changes still animate.
      if (snapResetTimer) clearTimeout(snapResetTimer);
      snapResetTimer = setTimeout(() => setSnap(false), 150);
    };

    const ro = new ResizeObserver(measureSnap);
    if (containerRef.current) ro.observe(containerRef.current);
    Object.values(tabRefs.current).forEach((el) => {
      if (el) ro.observe(el);
    });
    window.addEventListener('resize', measureSnap);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureSnap);
      if (snapResetTimer) clearTimeout(snapResetTimer);
    };
  }, [activeTab, tabs]);

  const handleKeyDown = (e: React.KeyboardEvent, tab: PillTab) => {
    if (tab.disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(tab.id);
    }
  };

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label="Navigation tabs"
      className={`relative inline-flex gap-1 md:gap-2 p-1 bg-vb-bg-secondary dark:bg-vb-bg-secondary-dark rounded-full max-w-full overflow-x-auto scrollbar-hide ${className}`}
    >
      {/* Sliding indicator — animates on tab change, snaps during resize so it tracks the layout */}
      <div
        className={`absolute top-1 bottom-1 left-0 bg-transparent border-2 border-vb-primary rounded-full pointer-events-none ${
          isReady && !snap ? 'transition-all duration-300 ease-in-out' : ''
        }`}
        style={{
          width: indicatorStyle.width,
          transform: `translateX(${indicatorStyle.left}px)`,
          opacity: isReady ? 1 : 0
        }}
      />

      {tabs.map((tab) => (
        <div
          key={tab.id}
          ref={(el) => (tabRefs.current[tab.id] = el)}
          role="tab"
          tabIndex={tab.disabled ? -1 : 0}
          aria-selected={activeTab === tab.id}
          aria-disabled={tab.disabled || undefined}
          title={tab.disabled ? tab.tooltip : undefined}
          onClick={() => !tab.disabled && onChange(tab.id)}
          onKeyDown={(e) => handleKeyDown(e, tab)}
          className={`relative z-10 px-3 md:px-5 py-1 md:py-1.5 text-xs md:text-sm font-medium rounded-full transition-colors select-none flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${
            tab.disabled
              ? 'text-vb-text-secondary/50 dark:text-vb-text-secondary-dark/50 cursor-not-allowed'
              : activeTab === tab.id
                ? 'text-vb-text-primary dark:text-vb-text-primary-dark cursor-pointer'
                : 'text-vb-text-secondary hover:text-vb-text-primary dark:text-vb-text-secondary-dark dark:hover:text-vb-text-primary-dark cursor-pointer'
          }`}
        >
          {tab.label}
          {tab.badge && <span>{tab.badge}</span>}
        </div>
      ))}
    </div>
  );
};
